import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {getCommaSeparatedList} from 'nutshell-core/get-comma-separated-list';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {colors} from 'shells/colors/colors';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TrashIcon} from 'shells/icon/svg/trash-icon';
import {Link} from 'shells/link/link';
import {DisplayOnlyTable} from 'shells/table/display-only-table';
import {Body} from 'shells/typography/body';
import {shouldShowDelimiter} from 'shells/utils/should-show-delimiter';

import type {GetManagedMeetingsTemplates_managedMeetings as ManagedMeeting} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {EmptyState} from './empty-state';
import {getStatusBadge} from './helpers';
import {ManagedMeetingsFeatureGatePage} from './managed-meetings-feature-gate-page';

type Props = {
    isLoading: boolean;
    isFirstLoad: boolean;
    meetings: ManagedMeeting[];
    isFiltering: boolean;
    error: string | null | undefined;
    onDeleteMeeting: (id: string, shouldShowConfirmation: boolean) => void;
    onUndeleteMeeting: (id: string) => void;
    hasSchedulerFullAccess: boolean;
};

export function ManagedMeetingsTable(props: Props) {
    const specialStates = [
        {
            shouldRender: () =>
                Boolean(
                    props.meetings &&
                        !props.meetings.length &&
                        !props.isLoading &&
                        props.isFiltering
                ),
            component: (
                <NothingFoundEmptyState
                    title='No meetings were found'
                    subtitle='Try loosening your search!'
                />
            ),
        },
        {
            shouldRender: () =>
                Boolean(props.meetings && !props.meetings.length && !props.isLoading),
            component: <EmptyState />,
        },
        {
            shouldRender: () => !props.isLoading && !props.hasSchedulerFullAccess,
            component: (
                <div className='flex align-center justify-center'>
                    <ManagedMeetingsFeatureGatePage />
                </div>
            ),
        },
    ];

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (meeting: ManagedMeeting) => meeting,
                dataType: 'component',
                hasLeftBorder: true,
                shouldTruncate: true,
                titleKey: 'name',
                width: 200,
                Cell: ({cell}: any) => {
                    const meeting = cell.value;
                    if (meeting.deletedTime) {
                        return <Body size='small'>{meeting.name}</Body>;
                    }

                    return (
                        <Link
                            to={meeting.htmlUrlPath}
                            // @ts-expect-error
                            as={RoutedLink}
                            variant='primary'
                            size='small'
                        >
                            {meeting.name}
                        </Link>
                    );
                },
            },
            {
                id: 'durationMinutes',
                Header: 'Duration',
                accessor: (meeting: ManagedMeeting) => meeting.durationMinutes,
                dataType: 'duration',
            },
            {
                id: 'status',
                Header: 'Status',
                accessor: (meeting: ManagedMeeting) => getStatusBadge(meeting),
                dataType: 'statusBadge',
            },
            {
                id: 'assignment',
                Header: 'Assignment',
                accessor: (meeting: ManagedMeeting) => meeting.assignments,
                dataType: 'component',
                width: 150,
                Cell: ({cell}: any) => {
                    return (
                        <div
                            className='truncate'
                            title={getCommaSeparatedList(
                                cell.value.edges.map(
                                    // @ts-expect-error
                                    (assignee) => assignee.node.assigneeEntity.name
                                )
                            )}
                        >
                            {/* @ts-expect-error */}
                            {cell.value.edges.map((assignee, index) => (
                                <span key={assignee.node.id}>
                                    <span style={{color: colors.textUsers}}>
                                        {assignee.node.assigneeEntity.name}
                                    </span>
                                    {shouldShowDelimiter(index, cell.value.edges.length) ? (
                                        <>,&nbsp;</>
                                    ) : undefined}
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                id: 'activityType',
                Header: 'Activity type',
                accessor: (meeting: ManagedMeeting) => meeting.activityType.name,
                dataType: 'string',
                width: 110,
            },
        ],
        []
    );

    return (
        <DisplayOnlyTable
            // @ts-expect-error
            columns={columns}
            data={props.meetings}
            isLoading={props.isLoading}
            isFirstLoad={props.isFirstLoad}
            error={props.error}
            fullWidth={true}
            disableSorting={false}
            specialStates={specialStates}
            isResizable={true}
            emptyStateText='No meetings were found'
            emptyStateSubtitle='Try loosening your search!'
            // @ts-expect-error
            renderRowActionMenu={(row) =>
                rowActionMenu(row, props.onDeleteMeeting, props.onUndeleteMeeting)
            }
        />
    );
}

const rowActionMenu = (
    row:
        | {
              original: ManagedMeeting;
          }
        | null
        | undefined,
    onDeleteMeeting: (id: string, shouldShowConfirmation: boolean) => void,
    onUndeleteMeeting: (id: string) => void
) => {
    if (!row || !row.original) {
        return null;
    }
    const isArchived = Boolean(row.original.deletedTime);

    const options = [];
    if (!isArchived) {
        options.push({
            label: 'View',
            icon: (
                <div className='pt-4'>
                    <EyeIcon size={18} />
                </div>
            ),
            handleSelect: () => appHistory.push(row.original.htmlUrlPath),
        });
        options.push({
            label: 'Archive',
            icon: (
                <div className='pt-4'>
                    <TrashIcon size={18} />
                </div>
            ),
            handleSelect: () => {
                onDeleteMeeting(
                    row.original.id,
                    Boolean(row.original.assignments && row.original.assignments.totalCount > 0)
                );
            },
        });
    } else {
        options.push({
            label: 'Restore',
            icon: (
                <div className='pt-4'>
                    <ArchiveIcon size={18} />
                </div>
            ),
            handleSelect: () => {
                onUndeleteMeeting(row.original.id);
            },
        });
    }

    return (
        <div className='px-4'>
            <ActionMenu padding='less' hasHoverBackground={true} options={options} size='small' />
        </div>
    );
};
